import {
  DiagnosticsVisual,
  LiveVisual,
  TripsVisual,
} from '../components/Visuals';

/**
 * Three alternating capability sections, each with one graphic.
 *
 * This replaces a flat eight-tile grid where "brick-level pack analysis" got
 * exactly the same visual weight as "passkey sign-in" — which is why the
 * diagnostics story, the part that actually differentiates Garage, used to be
 * invisible. The long tail moves to a condensed grid underneath.
 *
 * Every claim below is implemented today. Nothing here promises accuracy,
 * uptime or coverage the app doesn't have.
 */

interface Block {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  visual: () => JSX.Element;
}

const BLOCKS: Block[] = [
  {
    id: 'live',
    eyebrow: 'Live',
    title: 'Watch it happen, second by second',
    body: 'Your car streams to Garage as things happen — no polling, no pull-to-refresh. Pin any signal to a chart and watch it move.',
    points: [
      'Streaming speed, battery, power, temperatures and more',
      'Live location on a map, and every past drive replayed',
      'Vehicle alerts as they arrive, with full history',
    ],
    visual: LiveVisual,
  },
  {
    id: 'history',
    eyebrow: 'History',
    title: 'Every drive and every charge, kept',
    body: 'Every drive is logged on its own, start to finish, with the route it took. Your Wall Connector charging shows up in the same place.',
    points: [
      'Distance, duration, energy used and Wh/mi per trip',
      'The route each drive actually took',
      'Charging sessions with energy, duration and estimated cost',
    ],
    visual: TripsVisual,
  },
  {
    id: 'diagnostics',
    eyebrow: 'Diagnostics',
    title: 'The data the app never shows you',
    body: 'Where most dashboards stop: how the battery pack is really holding up, how hot each motor runs, and what the high-voltage system is doing.',
    points: [
      'Cell-group balance, capacity and battery health over time',
      'Per-motor stator, inverter and heatsink temperatures',
      'High-voltage isolation resistance, tracked against pack temperature',
      'Tire-sensor health, firmware status and how many keys are paired',
    ],
    visual: DiagnosticsVisual,
  },
];

const Check = (): JSX.Element => (
  <span aria-hidden className="mt-0.5 shrink-0 text-accent">
    ✓
  </span>
);

export default function Capabilities(): JSX.Element {
  return (
    <section id="features" className="container-page py-20 md:py-28">
      <div className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">What you get</p>
        <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          A real instrument panel for your car
        </h2>
        <p className="mt-4 text-lg text-muted">
          Not a prettier version of the Tesla app — the telemetry underneath it.
        </p>
      </div>

      <div className="mt-16 space-y-20 md:space-y-28">
        {BLOCKS.map((b, i) => (
          <div
            key={b.id}
            id={b.id}
            className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
          >
            {/* Alternate sides on wide screens; graphic always follows the
                prose in DOM order so it reads correctly on mobile and to a
                screen reader. */}
            <div className={i % 2 === 1 ? 'lg:order-2' : undefined}>
              <p className="eyebrow">{b.eyebrow}</p>
              <h3 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
                {b.title}
              </h3>
              <p className="mt-4 text-muted">{b.body}</p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {b.points.map((p) => (
                  <li key={p} className="flex gap-2.5">
                    <Check />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={i % 2 === 1 ? 'lg:order-1' : undefined}>
              <b.visual />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
